import { useState, useEffect, useCallback } from 'react';
import {
    isPushNotificationSupported,
    checkNotificationPermission,
    askPermission,
    registerServiceWorker,
    getVapidPublicKey,
    subscribeToPush,
    unsubscribeFromPush,
    getSubscription,
    sendSubscriptionToServer,
    removeSubscriptionFromServer,
    updateSubscriptionOnServer,
} from '../utils/pushNotification';

const STATES = {
    UNSUPPORTED: 'unsupported',
    LOADING: 'loading',
    PERMISSION_DENIED: 'permission_denied',
    PERMISSION_DEFAULT: 'permission_default',
    PERMISSION_GRANTED: 'permission_granted',
    SUBSCRIBED: 'subscribed',
    UNSUBSCRIBED: 'unsubscribed',
    ERROR: 'error',
};

export function usePushNotification() {
    const [state, setState] = useState(STATES.LOADING);
    const [error, setError] = useState(null);
    const [vapidPublicKey, setVapidPublicKey] = useState(null);

    // Check support and initialize
    useEffect(() => {
        let mounted = true;

        async function initialize() {
            try {
                // Check if push notifications are supported
                if (!isPushNotificationSupported()) {
                    if (mounted) setState(STATES.UNSUPPORTED);
                    return;
                }

                // Get VAPID public key
                try {
                    const key = await getVapidPublicKey();
                    if (mounted) setVapidPublicKey(key);
                } catch (err) {
                    console.error('Failed to get VAPID key:', err);
                    if (mounted) {
                        setState(STATES.ERROR);
                        setError('Failed to get VAPID public key');
                    }
                    return;
                }

                // Register service worker
                try {
                    await registerServiceWorker();
                } catch (err) {
                    console.error('Failed to register service worker:', err);
                    if (mounted) {
                        setState(STATES.ERROR);
                        setError('Failed to register service worker');
                    }
                    return;
                }

                // Check permission
                const permission = checkNotificationPermission();
                if (mounted) {
                    if (permission === 'denied') {
                        setState(STATES.PERMISSION_DENIED);
                    } else if (permission === 'granted') {
                        // Check if already subscribed
                        checkSubscription();
                    } else {
                        setState(STATES.PERMISSION_DEFAULT);
                    }
                }
            } catch (err) {
                console.error('Push notification initialization error:', err);
                if (mounted) {
                    setState(STATES.ERROR);
                    setError(err.message);
                }
            }
        }

        async function checkSubscription() {
            try {
                const subscription = await getSubscription();
                if (subscription) {
                    // Update subscription on server
                    try {
                        await updateSubscriptionOnServer(subscription);
                        if (mounted) setState(STATES.SUBSCRIBED);
                    } catch (err) {
                        console.error('Failed to update subscription:', err);
                        // Still set as subscribed if subscription exists
                        if (mounted) setState(STATES.SUBSCRIBED);
                    }
                } else {
                    if (mounted) setState(STATES.PERMISSION_GRANTED);
                }
            } catch (err) {
                console.error('Failed to check subscription:', err);
                if (mounted) setState(STATES.PERMISSION_GRANTED);
            }
        }

        initialize();

        return () => {
            mounted = false;
        };
    }, []);

    /**
     * Request permission and subscribe
     */
    const subscribe = useCallback(async () => {
        if (!vapidPublicKey) {
            setError('VAPID public key not available');
            return false;
        }

        setState(STATES.LOADING);
        setError(null);

        try {
            // Request permission
            const permission = await askPermission();
            if (permission !== 'granted') {
                setState(STATES.PERMISSION_DENIED);
                setError('Notification permission denied');
                return false;
            }

            // Subscribe to push
            const subscription = await subscribeToPush(vapidPublicKey);

            // Send subscription to server
            await sendSubscriptionToServer(subscription, 'POST');

            setState(STATES.SUBSCRIBED);
            return true;
        } catch (err) {
            console.error('Subscribe error:', err);
            setState(STATES.ERROR);
            setError(err.message || 'Failed to subscribe');
            return false;
        }
    }, [vapidPublicKey]);

    /**
     * Unsubscribe from push notifications
     */
    const unsubscribe = useCallback(async () => {
        setState(STATES.LOADING);
        setError(null);

        try {
            const subscription = await getSubscription();
            if (subscription) {
                // Remove from server
                await removeSubscriptionFromServer(subscription);
                // Unsubscribe from browser
                await unsubscribeFromPush();
            }

            setState(STATES.UNSUBSCRIBED);
            return true;
        } catch (err) {
            console.error('Unsubscribe error:', err);
            setState(STATES.ERROR);
            setError(err.message || 'Failed to unsubscribe');
            return false;
        }
    }, []);

    /**
     * Toggle subscription (subscribe if unsubscribed, unsubscribe if subscribed)
     */
    const toggle = useCallback(async () => {
        if (state === STATES.SUBSCRIBED) {
            return await unsubscribe();
        } else {
            return await subscribe();
        }
    }, [state, subscribe, unsubscribe]);

    return {
        state,
        error,
        isSupported: state !== STATES.UNSUPPORTED,
        isSubscribed: state === STATES.SUBSCRIBED,
        isPermissionDenied: state === STATES.PERMISSION_DENIED,
        isPermissionDefault: state === STATES.PERMISSION_DEFAULT,
        isLoading: state === STATES.LOADING,
        subscribe,
        unsubscribe,
        toggle,
        STATES,
    };
}

